/**
 * Validate BDSA metadata for a single item (for approve).
 * @param {Object} item - Item from doc.items with bdsaData
 * @returns {{isValid: boolean, errors: string[]}}
 */
export function validateItemForApprove(item) {
  const errors = [];
  const bdsa = item.bdsaData || {};

  const regions = bdsa.regions || [];
  if (!bdsa.blockId) {
    errors.push('Block is required');
  } else if (!Array.isArray(regions) || regions.length === 0) {
    errors.push('At least one region is required');
  }

  // Stain: must be present
  if (!bdsa.stain || !bdsa.stain._id) {
    errors.push('Stain is required');
  }
  
  // Magnification: must be present (string "40x" or "unknown_mag")
  const mag = bdsa.magnification;
  if (!mag || (mag !== 'unknown_mag' && !mag.endsWith('x'))) {
    errors.push('Magnification is required');
  }
  
  // Level/Scan: if present, must be >= 1 (should already be enforced by UI min=1)
  if (bdsa.level !== undefined && bdsa.level < 1) {
    errors.push('Level must be >= 1');
  }
  if (bdsa.scan !== undefined && bdsa.scan < 1) {
    errors.push('Scan must be >= 1');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Compute normalized filename for an item (for duplicate detection).
 * Mirrors backend get_normalized_item_name logic.
 * @param {Object} caseDoc - Case document with networkIdentifier
 * @param {Object} item - Item with bdsaData
 * @returns {string} Normalized filename (without .ext)
 */
export function computeNormalizedFilename(caseDoc, item) {
  const bdsa = item.bdsaData || {};

  // Use backend-computed networkFilename when available — it is the authoritative value
  // and avoids the need to look up stain abbreviations client-side (which requires schema
  // data not present on bdsaData.stain objects coming from doc.items).
  if (bdsa.networkFilename) {
    return bdsa.networkFilename;
  }

  const parts = [caseDoc.networkIdentifier];
  
  // Regions: join all abbreviations with +
  const regions = bdsa.regions || [];
  if (regions.length > 0) {
    const regionAbbrevs = regions.map(r => {
      if (typeof r === 'object' && r) {
        return r.abbreviation || r.displayName || r.name || '';
      }
      return String(r || '');
    }).filter(Boolean);
    if (regionAbbrevs.length > 0) {
      parts.push(regionAbbrevs.join('+'));
    }
  }
  
  // Stain: use abbreviation from schema (requires schemaStainKey and schemaId)
  // NOTE: This mirrors backend logic in naming.py - consider calling backend API instead
  // for a single source of truth. This function is used for frontend validation/preview only.
  if (bdsa.stain) {
    const stain = bdsa.stain;
    if (!stain.schemaStainKey || !stain.schemaId) {
      // Missing required fields - cannot compute normalized name
      console.warn('Stain missing schemaStainKey or schemaId, cannot compute normalized filename', stain);
      // Don't add stain to filename if we can't get abbreviation
    } else {
      // Try to get abbreviation from embedded schema if available
      // (stains may be enriched with schema data when retrieved from backend)
      let stainAbbrev = null;
      if (stain.schema && typeof stain.schema === 'object') {
        const stainDef = stain.schema[stain.schemaStainKey];
        if (stainDef && stainDef.abbreviation) {
          stainAbbrev = stainDef.abbreviation;
        }
      }
      
      if (!stainAbbrev) {
        // If schema not embedded, we can't look it up here
        // This is a limitation - ideally should call backend API
        console.warn('Stain schema not embedded, cannot get abbreviation. Consider calling backend API.', stain);
        // Don't add stain to filename if we can't get abbreviation
      } else {
        parts.push(stainAbbrev);
      }
    }
  }
  
  // Magnification: "40x" or "00x"
  const mag = bdsa.magnification || '00x';
  parts.push(mag);
  
  // Level/Scan suffix
  const level = bdsa.level || 1;
  const scan = bdsa.scan || 1;
  if (level > 1 && scan > 1) {
    parts.push(`L${level}S${scan}`);
  } else if (level > 1) {
    parts.push(`L${level}`);
  } else if (scan > 1) {
    parts.push(`S${scan}`);
  }
  
  return parts.join('-');
}

/**
 * Detect duplicate normalized filenames within a case's items.
 * @param {Object} caseDoc - Case document
 * @param {Array} items - Array of items from doc.items
 * @returns {Map<string, Array<Object>>} Map of normalized filename -> [items with that name]
 */
export function detectDuplicateFilenames(caseDoc, items) {
  const filenameMap = new Map();
  
  items.forEach(item => {
    const filename = computeNormalizedFilename(caseDoc, item);
    if (!filenameMap.has(filename)) {
      filenameMap.set(filename, []);
    }
    filenameMap.get(filename).push(item);
  });
  
  // Filter to only duplicates (length > 1)
  const duplicates = new Map();
  filenameMap.forEach((items, filename) => {
    if (items.length > 1) {
      duplicates.set(filename, items);
    }
  });
  
  return duplicates;
}

/**
 * Build a pseudo-item for computeNormalizedFilename from a table row / pending file.
 * @param {{regions?: Array, stain?: object, mag?: string, magnification?: string, level?: number, scan?: number, networkFilename?: string}} source
 * @returns {{bdsaData: object}}
 */
export function toNamingItem(source = {}) {
  return {
    bdsaData: {
      regions: Array.isArray(source.regions) ? source.regions : [],
      stain: source.stain || null,
      magnification: source.mag ?? source.magnification ?? '00x',
      level: source.level ?? 1,
      scan: source.scan ?? 1,
      networkFilename: source.networkFilename || null,
    },
  };
}

/**
 * Annotate row objects with mappedFilename + filenameCollision for Case Builder UI.
 * Mutates rows in place and returns collision groups (mapped name -> count).
 * @param {object} caseDoc
 * @param {Array<object>} rows
 * @returns {Map<string, number>} colliding mapped filenames -> count
 */
export function annotateMappedFilenameCollisions(caseDoc, rows) {
  const counts = new Map();
  (rows || []).forEach((row) => {
    const fromItem = row.itemObj
      ? computeNormalizedFilename(caseDoc, row.itemObj)
      : null;
    const fromRow = computeNormalizedFilename(
      caseDoc,
      toNamingItem({
        ...row,
        networkFilename: row.itemObj?.bdsaData?.networkFilename || row.networkFilename,
      })
    );
    const mapped = fromItem || fromRow || '';
    row.mappedFilename = mapped;
    if (!mapped) return;
    counts.set(mapped, (counts.get(mapped) || 0) + 1);
  });

  const collisions = new Map();
  counts.forEach((count, filename) => {
    if (count > 1) collisions.set(filename, count);
  });

  (rows || []).forEach((row) => {
    row.filenameCollision = !!(row.mappedFilename && collisions.has(row.mappedFilename));
  });

  return collisions;
}

/**
 * Validate items for batch approval, separating into validated, incomplete, and duplicates.
 * @param {Object} caseDoc - Case document with networkIdentifier
 * @param {Array} items - Array of items to validate
 * @returns {{validated: Array, incomplete: Array, duplicates: Map}}
 *   - validated: Items that pass validation and aren't duplicates
 *   - incomplete: Array of {item, errors} for items with validation errors
 *   - duplicates: Map of normalized filename -> [items with that name]
 */
export function validateItemsForBatchApprove(caseDoc, items) {
  const incomplete = [];
  const validated = [];
  
  // First pass: validate each item
  items.forEach(item => {
    const validation = validateItemForApprove(item);
    if (!validation.isValid) {
      incomplete.push({ item, errors: validation.errors });
    } else {
      validated.push(item);
    }
  });
  
  // Second pass: detect duplicates among validated items
  const duplicates = detectDuplicateFilenames(caseDoc, validated);
  
  // Third pass: remove duplicates from validated list
  const duplicateItemIds = new Set();
  duplicates.forEach(duplicateItems => {
    duplicateItems.forEach(item => duplicateItemIds.add(item._id));
  });
  
  const validatedWithoutDuplicates = validated.filter(item => !duplicateItemIds.has(item._id));
  
  return {
    validated: validatedWithoutDuplicates,
    incomplete,
    duplicates
  };
}
